import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import {
  Perm,
  type Receipt,
  type ReturnableLine,
  type Supplier,
  type SupplierReturn,
  type SupplierReturnDetail,
  type Warehouse,
} from '../api/types'
import { formatMoney, formatMoment, formatQty, toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Row, Select, Spinner, TextInput,
} from '@shop/ui'
import { returnStatusNames } from '../ui/purchasing'

/**
 * مرتجعاتُ المورّدين.
 *
 * <b>ولا تُعرض كميّةٌ أكبرُ من المتاح للإرجاع</b> — والشاشةُ تمنع قبل أن يصل
 * الطلبُ إلى الخادم. <b>وحارسُ القاعدة هو الحكمُ النهائيّ</b> على أيّ حال:
 * إرجاعان يُرحَّلان في اللحظة نفسها يمرّان من الشاشة ولا يمرّان من القفل.
 */
export default function SupplierReturns() {
  const { can } = useAuth()
  const [status, setStatus] = useState('')

  const returns = useQuery({
    queryKey: ['returns', status],
    queryFn: () => api<SupplierReturn[]>(`/api/v1/supplier-returns?status=${status}`),
  })

  return (
    <Panel
      title="مرتجعاتُ المورّدين"
      actions={can(Perm.returnCreate) && <NewReturnButton />}
    >
      <div className="mb-4">
        <Field label="الحالة">
          <Select value={status} onChange={setStatus}>
            <option value="">الكلّ</option>
            <option value="DRAFT">مسوّدة</option>
            <option value="POSTED">مُرحَّل</option>
            <option value="REVERSED">معكوس</option>
          </Select>
        </Field>
      </div>

      {returns.isPending && <Spinner />}
      {returns.isError && <ErrorBanner error={returns.error} />}
      {returns.data?.length === 0 && <Empty>لا مرتجعاتٍ مطابقة.</Empty>}

      <ul className="space-y-2">
        {(returns.data ?? []).map((document) => (
          <li key={document.id}>
            <Link
              to={`/supplier-returns/${document.id}`}
              className="block rounded-md border border-line px-3 py-2 hover:bg-surface"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{document.supplierName}</span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">{document.returnNo}</span>
              </div>

              <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                <span>{returnStatusNames[document.status] ?? document.status}</span>
                <span className="tabular">{formatQty(document.totalQty)} قطعة</span>
                <span className="tabular">{document.returnDate}</span>
              </div>

              <p className="mt-1 text-xs text-ink-soft">{document.reason}</p>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  )
}

function NewReturnButton() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [supplierId, setSupplierId] = useState('')
  const [warehouseId, setWarehouseId] = useState('')
  const [reason, setReason] = useState('')

  const suppliers = useQuery({
    queryKey: ['suppliers', '', false],
    queryFn: () => api<Supplier[]>('/api/v1/suppliers'),
    enabled: open,
  })

  const warehouses = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api<Warehouse[]>('/api/v1/warehouses'),
    enabled: open,
  })

  const create = useMutation({
    mutationFn: () =>
      api<SupplierReturn>('/api/v1/supplier-returns', {
        method: 'POST',
        body: { supplierId, warehouseId, reason },
      }),
    onSuccess: async (document) => {
      await queryClient.invalidateQueries({ queryKey: ['returns'] })
      setOpen(false)
      void navigate(`/supplier-returns/${document.id}`)
    },
  })

  if (!open) {
    return <Button tone="primary" onClick={() => setOpen(true)}>إرجاعٌ جديد</Button>
  }

  return (
    <div className="w-full space-y-3 rounded-md border border-line bg-surface p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="المورّد">
          <Select value={supplierId} onChange={setSupplierId}>
            <option value="">اختر مورّدًا…</option>
            {(suppliers.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.nameAr}</option>
            ))}
          </Select>
        </Field>

        <Field label="المستودع" hint="الذي تخرج منه البضاعة">
          <Select value={warehouseId} onChange={setWarehouseId}>
            <option value="">اختر مستودعًا…</option>
            {(warehouses.data ?? []).map((w) => (
              <option key={w.id} value={w.id}>{w.nameAr}</option>
            ))}
          </Select>
        </Field>
      </div>

      <Field label="سببُ الإرجاع" hint="عشرةُ أحرفٍ فأكثر — يُسأل عنه بعد سنة">
        <TextInput value={reason} onChange={setReason} placeholder="وصلت تالفةً من المصنع" />
      </Field>

      {create.isError && <ErrorBanner error={create.error} />}

      <div className="flex gap-2">
        <Button
          tone="primary"
          disabled={!supplierId || !warehouseId || reason.trim().length < 10 || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'يُنشأ…' : 'إنشاء'}
        </Button>

        <Button onClick={() => setOpen(false)}>إلغاء</Button>
      </div>
    </div>
  )
}

/** إرجاعٌ بتفاصيله وسطوره — ومعها ما يبقى قابلًا للإرجاع من كلّ إيصال. */
export function SupplierReturnDetailScreen() {
  const { id } = useParams<{ id: string }>()
  const { can } = useAuth()

  const document = useQuery({
    queryKey: ['return', id],
    queryFn: () => api<SupplierReturnDetail>(`/api/v1/supplier-returns/${id}`),
    enabled: Boolean(id),
  })

  if (document.isPending) {
    return <Spinner />
  }

  if (document.isError) {
    return <ErrorBanner error={document.error} />
  }

  const { header, lines } = document.data!
  const isDraft = header.status === 'DRAFT'

  return (
    <div className="space-y-4">
      <Panel title={`إرجاعٌ لمورّد ${header.returnNo}`}>
        <dl className="space-y-1 text-sm">
          <Row label="المورّد">{header.supplierName}</Row>
          <Row label="الحالة">{returnStatusNames[header.status] ?? header.status}</Row>
          <Row label="السبب">{header.reason}</Row>
          <Row label="التاريخ">{header.returnDate}</Row>
          {header.creditNoteNo && <Row label="إشعارٌ دائن">{header.creditNoteNo}</Row>}
          {can(Perm.costView) && <Row label="القيمة">{formatMoney(header.totalValue)}</Row>}
          {header.postedAt && <Row label="رُحِّل">{formatMoment(header.postedAt)}</Row>}
          {header.reverseReason && <Row label="سببُ العكس">{header.reverseReason}</Row>}
        </dl>

        <div className="mt-4 flex flex-wrap gap-2">
          {isDraft && can(Perm.returnPost) && lines.length > 0 && <PostControl returnId={header.id} />}
          {header.status === 'POSTED' && can(Perm.movementReverse) && (
            <ReverseControl returnId={header.id} />
          )}
        </div>

        {isDraft && !can(Perm.returnPost) && (
          <div className="mt-4">
            <Banner tone="info">
              الإرجاعُ مسوّدة. وترحيلُه — وهو ما يُخرج البضاعةَ من المخزن — يحتاج
              صلاحيّةً مستقلّة.
            </Banner>
          </div>
        )}
      </Panel>

      <Panel title="السطور">
        {lines.length === 0 && <Empty>لا سطورَ بعد.</Empty>}

        <ul className="space-y-2">
          {lines.map((line) => (
            <li key={line.id} className="rounded-md border border-line px-3 py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{line.productName}</span>
                <span className="tabular text-xs text-ink-soft" dir="ltr">{line.sku}</span>
              </div>

              <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-ink-soft">
                <span className="tabular">{formatQty(line.qty)}</span>
                {can(Perm.costView) && (
                  <span className="tabular">بكلفة {formatMoney(line.unitCost)}</span>
                )}
              </div>
            </li>
          ))}
        </ul>

        {isDraft && can(Perm.returnCreate) && (
          <div className="mt-3">
            <ReturnLineEditor document={header} lines={lines} />
          </div>
        )}
      </Panel>
    </div>
  )
}

type ReturnLineRow = SupplierReturnDetail['lines'][number]

type DraftLine = { receiptLineId: string; label: string; max: number; qty: string }

/**
 * اختيارُ إيصالٍ سابقٍ ثمّ ما يُرجَع منه.
 *
 * <b>ولا يبحث الموظّفُ عن صنف</b>: يختار ممّا استُلم فعلًا من هذا المورّد،
 * ويرى المستلَمَ والمُرجَعَ سابقًا والمتبقّي — فلا يطلب ما لا يوجد.
 */
function ReturnLineEditor({ document, lines }: { document: SupplierReturn; lines: ReturnLineRow[] }) {
  const queryClient = useQueryClient()
  const [receiptId, setReceiptId] = useState('')

  const [draft, setDraft] = useState<DraftLine[]>(() =>
    lines.map((l) => ({
      receiptLineId: l.receiptLineId,
      label: `${l.productName} · ${l.sku}`,
      max: l.qty,
      qty: l.qty.toString(),
    })),
  )

  // إيصالاتُ هذا المورّد المُرحَّلة — ولا يُرجَع إلا ممّا استُلم منه.
  const receipts = useQuery({
    queryKey: ['receipts', 'POSTED', document.supplierId],
    queryFn: () => api<Receipt[]>(
      `/api/v1/receipts?status=POSTED&supplierId=${document.supplierId}`),
  })

  const returnable = useQuery({
    queryKey: ['returnable', receiptId],
    queryFn: () => api<ReturnableLine[]>(`/api/v1/supplier-returns/returnable/${receiptId}`),
    enabled: receiptId.length > 0,
  })

  const save = useMutation({
    mutationFn: () =>
      api<SupplierReturnDetail>(`/api/v1/supplier-returns/${document.id}/lines`, {
        method: 'PUT',
        body: {
          lines: draft.map((d) => ({ receiptLineId: d.receiptLineId, qty: toNumber(d.qty) ?? 0 })),
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['return', document.id] })
    },
  })

  // ⚠ الشاشةُ تمنع تجاوزَ المتاح — **والقاعدةُ هي الحكمُ النهائيّ**: سطرٌ قد
  //   يُرجَع من مستندٍ آخر بين فتح الشاشة وحفظها.
  const overflow = draft.some((d) => (toNumber(d.qty) ?? 0) > d.max)
  const incomplete = draft.some((d) => (toNumber(d.qty) ?? 0) <= 0)

  const available = (returnable.data ?? []).filter(
    (line) => !draft.some((d) => d.receiptLineId === line.receiptLineId),
  )

  return (
    <div className="space-y-3">
      {draft.map((line, index) => (
        <div key={line.receiptLineId} className="rounded-md border border-line p-3">
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <span className="text-sm font-medium">{line.label}</span>
            <Button onClick={() => setDraft(draft.filter((_, i) => i !== index))}>حذف</Button>
          </div>

          <Field
            label="الكميّةُ المُرجَعة"
            hint={`المتاحُ للإرجاع ${formatQty(line.max)}`}
          >
            <NumberInput
              value={line.qty}
              onChange={(v) => setDraft(draft.map((d, i) => (i === index ? { ...d, qty: v } : d)))}
            />
          </Field>

          {(toNumber(line.qty) ?? 0) > line.max && (
            <p className="mt-1 text-xs text-danger">
              أكثرُ من المتاح — والمتاحُ {formatQty(line.max)}.
            </p>
          )}
        </div>
      ))}

      <div className="rounded-md border border-dashed border-line p-3">
        <Field label="اختر إيصالًا سابقًا" hint="ولا يُرجَع إلا ممّا استُلم من هذا المورّد">
          <Select value={receiptId} onChange={setReceiptId}>
            <option value="">اختر إيصالًا…</option>
            {(receipts.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.receiptNo} — {r.receiptDate}
                {r.supplierInvoiceNo ? ` · فاتورة ${r.supplierInvoiceNo}` : ''}
              </option>
            ))}
          </Select>
        </Field>

        {receiptId && returnable.isPending && <Spinner />}
        {receiptId && available.length === 0 && !returnable.isPending && (
          <Empty>لا شيءَ يبقى قابلًا للإرجاع من هذا الإيصال.</Empty>
        )}

        <ul className="mt-2 space-y-1">
          {available.map((line) => (
            <li key={line.receiptLineId}>
              <button
                type="button"
                className="w-full rounded-md px-2 py-1 text-start text-sm hover:bg-surface"
                onClick={() =>
                  setDraft([...draft, {
                    receiptLineId: line.receiptLineId,
                    label: `${line.productName} · ${line.sku}`,
                    max: line.returnableQty,
                    qty: line.returnableQty.toString(),
                  }])
                }
              >
                {line.productName}
                <span className="tabular mx-2 text-xs text-ink-soft">
                  استُلم {formatQty(line.receivedQty)}
                  {line.returnedQty > 0 && ` · أُرجع ${formatQty(line.returnedQty)}`}
                  {' · '}المتاح {formatQty(line.returnableQty)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {save.isError && <ErrorBanner error={save.error} />}

      <Button
        tone="primary"
        disabled={draft.length === 0 || incomplete || overflow || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'تُحفظ…' : 'حفظُ السطور'}
      </Button>
    </div>
  )
}

function PostControl({ returnId }: { returnId: string }) {
  const queryClient = useQueryClient()

  const post = useMutation({
    mutationFn: () =>
      api<SupplierReturnDetail>(`/api/v1/supplier-returns/${returnId}/post`, { method: 'POST', body: {} }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['return', returnId] })
      await queryClient.invalidateQueries({ queryKey: ['returns'] })
      await queryClient.invalidateQueries({ queryKey: ['stock'] })
    },
  })

  return (
    <div className="w-full space-y-2">
      <Banner tone="warn">
        الترحيلُ يُخرج البضاعةَ من المخزن بكلفة شرائها الأصليّة. <b>ولا يُعدَّل
        بعده</b> — يُصحَّح بعكسٍ لا بتعديل.
      </Banner>

      {post.isError && <ErrorBanner error={post.error} />}

      <Button tone="primary" disabled={post.isPending} onClick={() => post.mutate()}>
        {post.isPending ? 'يُرحَّل…' : 'ترحيلُ الإرجاع'}
      </Button>
    </div>
  )
}

/** التراجعُ <b>بحركةٍ جديدة</b> مرتبطةٍ بالأصل — لا بتعديل القديمة. */
function ReverseControl({ returnId }: { returnId: string }) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [asking, setAsking] = useState(false)

  const reverse = useMutation({
    mutationFn: () =>
      api<SupplierReturnDetail>(`/api/v1/supplier-returns/${returnId}/reverse`, {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['return', returnId] })
      await queryClient.invalidateQueries({ queryKey: ['stock'] })
      setAsking(false)
    },
  })

  if (!asking) {
    return <Button onClick={() => setAsking(true)}>التراجعُ عن الإرجاع</Button>
  }

  return (
    <div className="w-full space-y-2">
      <Banner tone="warn">
        البضاعةُ تعود إلى المخزن <b>بحركةٍ جديدةٍ تشير إلى الأصل</b> — ولا تُمحى
        حركةُ الإرجاع ولا تُعدَّل.
      </Banner>

      <Field label="سببُ التراجع" hint="عشرةُ أحرفٍ فأكثر">
        <TextInput value={reason} onChange={setReason} placeholder="المورّدُ رفض استلامَ المرتجع" />
      </Field>

      {reverse.isError && <ErrorBanner error={reverse.error} />}

      <div className="flex gap-2">
        <Button
          tone="danger"
          disabled={reason.trim().length < 10 || reverse.isPending}
          onClick={() => reverse.mutate()}
        >
          {reverse.isPending ? 'يُعكَس…' : 'تأكيدُ التراجع'}
        </Button>

        <Button onClick={() => setAsking(false)}>تراجع</Button>
      </div>
    </div>
  )
}
